import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';

interface ImageItem {
  url: string;
  fileName: string;
  fileSize: number;
}

interface Props {
  images: ImageItem[];
  initialIndex: number;
  onClose: () => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function ImageLightbox({ images, initialIndex, onClose }: Props) {
  const [index, setIndex] = useState(initialIndex);
  const current = images[index];
  const hasPrev = index > 0;
  const hasNext = index < images.length - 1;

  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) setIndex((i) => i - 1);
      if (e.key === 'ArrowRight' && hasNext) setIndex((i) => i + 1);
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onClose, hasPrev, hasNext]);

  if (!current) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button onClick={onClose}
          className="absolute top-2 right-2 z-10 text-white/80 hover:text-white bg-black/40 rounded-full p-1.5">
          <X className="size-5" />
        </button>

        {/* Image */}
        <img src={current.url} alt={current.fileName}
          className="max-w-full max-h-[85vh] object-contain rounded" />

        {/* Navigation */}
        {hasPrev && (
          <button onClick={() => setIndex((i) => i - 1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 rounded-full p-2">
            <ChevronLeft className="size-6" />
          </button>
        )}
        {hasNext && (
          <button onClick={() => setIndex((i) => i + 1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 rounded-full p-2">
            <ChevronRight className="size-6" />
          </button>
        )}

        {/* Info bar */}
        <div className="mt-2 bg-black/50 text-white/90 rounded-lg px-4 py-2 text-sm flex items-center gap-3">
          <span>{current.fileName}</span>
          <span className="text-white/60">{formatSize(current.fileSize)}</span>
          <a href={current.url} download={current.fileName}
            className="flex items-center gap-1 text-white/80 hover:text-white">
            <Download className="size-4" /> 다운로드
          </a>
        </div>
      </div>
    </div>
  );
}
